/**
 * Account State Management — V4 Only
 *
 * V4: Classrooms promoted to top-level AccountsState.classrooms (canonical source).
 *
 * V3 migration code removed (9 april 2026) — no active V3 users.
 * dataLayer migration D2: loadState/saveState now use dataLayer (async).
 */

import { AppState } from '../../app/types'
import { AccountsState } from '../types'
import { validateAccountsState } from './queries'
import { debug, warn, error, safeJsonParse } from '../../utils/logger'
import { dataLayer } from '../../data/dataLayer'

export const CURRENT_SCHEMA_VERSION = 4

/**
 * Build learnerToTeachers reverse mapping from teacherToLearners.
 * Used for backward compatibility when loading state that predates P1-2.
 */
function buildLearnerToTeachers(
  teacherToLearners: Record<string, string[]>,
): Record<string, string[]> {
  const result: Record<string, string[]> = {}
  for (const [teacherId, learnerIds] of Object.entries(teacherToLearners)) {
    for (const learnerId of learnerIds) {
      if (!result[learnerId]) result[learnerId] = []
      if (!result[learnerId].includes(teacherId)) {
        result[learnerId].push(teacherId)
      }
    }
  }
  return result
}

/**
 * Build learnerToClassrooms reverse mapping from classrooms.
 * Used for self-healing state if the index was dropped by the Wave D P0 bug.
 */
function buildLearnerToClassrooms(
  classrooms: AccountsState['classrooms'],
): Record<string, string[]> {
  const result: Record<string, string[]> = {}
  for (const [classroomId, classroom] of Object.entries(classrooms)) {
    for (const learnerId of classroom.learnerIds) {
      if (!result[learnerId]) result[learnerId] = []
      if (!result[learnerId].includes(classroomId)) {
        result[learnerId].push(classroomId)
      }
    }
  }
  return result
}

/**
 * Multi-Account State V4 (current — classrooms as top-level entity)
 */
export interface MultiAccountStateV4 {
  schemaVersion: 4
  accounts: AccountsState
  learnerData: Record<string, AppState>
}

/**
 * Normalize V4 state (idempotent fixes for V4 data).
 */
function normalizeV4State(state: MultiAccountStateV4): MultiAccountStateV4 {
  const learnerData = Object.fromEntries(
    Object.entries(state.learnerData).map(([learnerId, appState]) => [
      learnerId,
      {
        ...appState,
        familyMastery: appState.familyMastery ?? {},
      },
    ]),
  ) as MultiAccountStateV4['learnerData']

  // Ensure learnerToTeachers exists
  const relationships = state.accounts.relationships
  const learnerToTeachers =
    relationships.learnerToTeachers ??
    buildLearnerToTeachers(relationships.teacherToLearners)

  // Wave 3.1: Backfill `coTeacherIds: []` on classrooms missing the field.
  // Existing stored state predates the co-teacher datamodel.
  const rawClassrooms = state.accounts.classrooms ?? {}
  const classrooms: typeof rawClassrooms = {}
  for (const [id, classroom] of Object.entries(rawClassrooms)) {
    classrooms[id] =
      classroom.coTeacherIds === undefined
        ? { ...classroom, coTeacherIds: [] }
        : classroom
  }

  // Self-heal learnerToClassrooms if it's missing or completely empty (P0 bug recovery)
  let learnerToClassrooms = relationships.learnerToClassrooms
  if (!learnerToClassrooms || Object.keys(learnerToClassrooms).length === 0) {
    const hasLearnersInClassrooms = Object.values(classrooms).some(
      (c) => c.learnerIds.length > 0,
    )
    if (hasLearnersInClassrooms) {
      learnerToClassrooms = buildLearnerToClassrooms(classrooms)
    } else {
      learnerToClassrooms = {}
    }
  }

  // Backfill hasCompletedOnboarding for any accounts missing it
  const accountsById = { ...state.accounts.accountsById }
  for (const [id, account] of Object.entries(accountsById)) {
    if (account.hasCompletedOnboarding === undefined) {
      accountsById[id] = { ...account, hasCompletedOnboarding: true }
    }
  }

  return {
    ...state,
    accounts: {
      ...state.accounts,
      accountsById,
      classrooms,
      relationships: {
        ...relationships,
        learnerToTeachers,
        learnerToClassrooms,
      },
    },
    learnerData,
  }
}

/**
 * Load state from dataLayer (V4 only).
 *
 * Unsupported schemas are discarded and reset to fresh state.
 */
export async function loadState(): Promise<MultiAccountStateV4> {
  try {
    const stored = await dataLayer.loadMultiAccountState()
    if (!stored) {
      debug('No stored state found - creating fresh V4 state')
      return createFreshState()
    }
    const parsed = safeJsonParse(stored, null, 'loadState')
    if (!parsed) {
      warn('Failed to parse stored state - creating fresh V4 state')
      return createFreshState()
    }

    // V4 format (current)
    if (parsed.schemaVersion === 4) {
      if (validateAccountsState(parsed.accounts)) {
        debug('Loaded existing V4 state')
        return normalizeV4State(parsed as MultiAccountStateV4)
      } else {
        warn('Invalid V4 accounts state structure - reset')
        return createFreshState()
      }
    }

    warn(
      `Unsupported schema (${parsed.schemaVersion || 'unknown'}) - creating fresh V4 state.`,
    )
    return createFreshState()
  } catch (err) {
    error('Failed to load state:', err)
    return createFreshState()
  }
}

/**
 * Create fresh V4 state.
 */
export function createFreshState(): MultiAccountStateV4 {
  return {
    schemaVersion: 4,
    accounts: {
      accountsById: {},
      activeAccountId: '',
      relationships: {
        parentToLearners: {},
        learnerToParents: {},
        teacherToLearners: {},
        learnerToTeachers: {},
        learnerToClassrooms: {},
      },
      classrooms: {},
    },
    learnerData: {},
  }
}

/**
 * Save state via dataLayer
 */
export async function saveState(state: MultiAccountStateV4): Promise<void> {
  const serialized = JSON.stringify(state)
  const success = await dataLayer.saveMultiAccountState(serialized)
  if (!success) {
    error(
      'Failed to save state: localStorage quota exceeded. Data may be lost.',
    )
  }
}
